import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Compass, ArrowLeft } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { Seo } from '@/client/components/Seo';
import { isLocale, type Locale } from '@/client/i18n';

export function NotFound() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';

  return (
    <div className="relative">
      <Seo
        title={t('seo.not_found_title')}
        description={t('seo.default_description')}
        locale={locale}
        index={false}
      />

      <div
        className="absolute top-10 -left-32 w-[26rem] h-[26rem] rounded-full pointer-events-none animate-float-orb"
        style={{
          background: 'radial-gradient(circle, rgba(193,167,226,0.40) 0%, transparent 65%)',
          filter: 'blur(40px)',
        }}
        aria-hidden
      />
      <div
        className="absolute top-40 -right-32 w-[28rem] h-[28rem] rounded-full pointer-events-none animate-float-orb"
        style={{
          background: 'radial-gradient(circle, rgba(117,59,189,0.18) 0%, transparent 65%)',
          filter: 'blur(48px)',
          animationDelay: '-12s',
        }}
        aria-hidden
      />

      <section className="relative mx-auto max-w-xl px-5 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="dr-card-strong p-10 sm:p-14 text-center animate-fade-up">
          <div className="relative mx-auto h-24 w-24 mb-6">
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  'conic-gradient(from 220deg, rgba(193,167,226,0.40), rgba(117,59,189,0.18), rgba(193,167,226,0.40))',
                filter: 'blur(8px)',
              }}
              aria-hidden
            />
            <div className="relative h-full w-full rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 flex items-center justify-center animate-float">
              <Compass size={42} className="text-primary" />
            </div>
          </div>

          <p className="dr-eyebrow">404</p>
          <h1 className="mt-3 font-display text-[clamp(2rem,5vw,3rem)] leading-[1.05] tracking-tight text-primary-dark">
            {t('errors.not_found_title')}
          </h1>
          <p className="mt-4 font-display text-body-large text-text-secondary leading-relaxed max-w-md mx-auto">
            {t('errors.not_found_body')}
          </p>

          <Link to={`/${locale}`} className="inline-block mt-8">
            <Button size="lg">
              <ArrowLeft size={16} />
              {t('errors.back_home')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
